import Color from 'color';

/**
 * Adds the given opacity to the given color value and returns a string representation of the result
 */
export function addOpacityToColor(colorString: string, opacity: number): string {
    const color = Color(colorString);
    return color.alpha(opacity).toString();
}

/**
 * Computes the result of overlapping multiple colors on top of each other. This only has meaning if the colors
 * overlapping have opacity to them- if the top color has alpha = 1.0, the result will just be the top color.
 *
 * this is called alpha compositing: https://en.wikipedia.org/wiki/Alpha_compositing, an implementation of the "over" operation
 * @param colors the colors to overlap. Descending order (first color is on top, second color is the second from the top, etc.)
 * @returns string representation of the resulting color.
 */
export function computeResultingOverlapColor(...colors: string[]): string {
    let total = Color(colors[colors.length - 1]);

    for (let i = colors.length - 2; i >= 0; i--) {
        const top = Color(colors[i]);
        const totalAlpha = top.alpha() + total.alpha() * (1 - top.alpha());
        total = Color({
            r: Math.floor(
                (top.alpha() * top.red() + total.alpha() * (1 - top.alpha()) * total.red()) /
                    totalAlpha
            ),
            g: Math.floor(
                (top.alpha() * top.green() + total.alpha() * (1 - top.alpha()) * total.green()) /
                    totalAlpha
            ),
            b: Math.floor(
                (top.alpha() * top.blue() + total.alpha() * (1 - top.alpha()) * total.blue()) /
                    totalAlpha
            ),
            alpha: totalAlpha,
        });
    }

    return total.toString();
}

/**
 * Converts the given color string to HSB (HSV), then adds the saturation to it. Saturation will be bounded
 * between [0, 100]
 */
export function addSaturationToColor(colorString: string, saturation: number): string {
    const color = Color(colorString);
    const existingS = color.saturationv();
    const newS = Math.max(Math.min(existingS + saturation, 100), 0);
    return color.saturationv(newS).toString();
}

/**
 * Lightens a provided color by changing its saturation and value
 * by the provided factor.
 */
export function lightenColor(hsv: Color, factor: number) {
    const s = hsv.saturationv();
    const v = hsv.value();
    hsv = hsv.saturationv(clamp(s - s * factor, 100, 0));
    hsv = hsv.value(clamp(v + (100 - v) * factor, 100, 0));
    return hsv;
}

/**
 * Darkens a provided color by changing its value
 * by the provided factor.
 */
export function darkenColor(hsv: Color, factor: number) {
    const v = hsv.value();
    hsv = hsv.value(clamp(v - v * factor, 100, 0));
    return hsv;
}

/**
 * Clamp a given color component's value to ensure it falls within a legal range.
 */
function clamp(value: number, max: number, min = 0): number {
    return value < min ? min : value > max ? max : value;
}
